<template>
  <div id="benefits-center-page">
    <div class="benefits-center-card">
      <header class="benefits-center-header">
        <h1>{{ t("benefitsCenter-title") }}</h1>
      </header>

      <section class="benefits-center-content">
        <BenefitsTabs v-model="activeTab" />

        <Loader v-if="state === 'loading'" :inline="true" />

        <div v-else class="benefits-center-layout">
          <div class="benefits-center-tab-content">
            <template v-if="activeTab === 'settings'">
              <BenefitsSettingsForm v-model="program" @save-section="saveSettingsSection" />
            </template>

            <template v-else-if="activeTab === 'points'">
              <BenefitsPointsSection v-model="program.points" @save-section="saveSettingsSection" @save-reward="saveReward" @delete-reward="deleteReward" />
            </template>

            <template v-else>
              <div class="benefits-center-empty-tab">
                {{ t("benefitsCenter-tabs-empty") }}
              </div>
            </template>
          </div>

          <aside class="benefits-center-preview benefits-center-preview--desktop">
            <BenefitsPreview :program="program" :place-slug="placeSlug" />
          </aside>
        </div>

        <button
          v-if="state !== 'loading'"
          type="button"
          class="benefits-center-preview-fab"
          :class="{ 'is-open': previewOpen }"
          :aria-label="previewOpen ? t('benefitsCenter-preview-close') : t('benefitsCenter-preview-open')"
          @click="togglePreview"
        >
          <Icon :icon-class="previewOpen ? 'ui-x-close' : 'ui-phone-01'" size="5" color="#ffffff" />
        </button>

        <transition name="preview-mobile">
          <div
            v-if="previewOpen"
            class="benefits-center-preview benefits-center-preview--mobile"
          >
            <div class="benefits-center-preview-mobile-body">
              <BenefitsPreview :program="program" :place-slug="placeSlug" fullscreen />
            </div>
          </div>
        </transition>
      </section>
    </div>
  </div>
</template>

<script>
import BenefitsTabs from "./benefits-tabs.vue";
import BenefitsSettingsForm from "./sections/settings/benefits-settings-form.vue";
import BenefitsPointsSection from "./sections/points/benefits-points-section.vue";
import BenefitsPreview from "./shared/benefits-preview.vue";
import Loader from "../components/loader/loader.vue";
import Icon from "../components/icon/icon.vue";
import keviApi from "../keviApi.js"
export default {
  name: "BenefitsCenter",
  components: {
    BenefitsTabs,
    BenefitsSettingsForm,
    BenefitsPointsSection,
    BenefitsPreview,
    Loader,
    Icon,
  },
  computed: {
    placeSlug() {
      return (this.$store.state.place && this.$store.state.place.slug) || "";
    },
  },
  data() {
    return {
      program: this.getDefaultProgram(),
      activeTab: (this.$route && this.$route.query && this.$route.query.tab) || "settings",
      state: "loading",
      previewOpen: false,
    };
  },
  watch: {
    activeTab(newTab) {
      if (this.$route && this.$route.query.tab !== newTab) {
        this.$router.replace({ query: { ...this.$route.query, tab: newTab } }).catch(() => {});
      }
    }
  },
  created() {
    const permissions = this.$store.state.user?.permissions || {};
    const hasPermission = this.$store.state.user?.global_admin || permissions.benefit_programs?.enabled;

    if (!hasPermission) {
      this.$store.dispatch("notification/error", {
        text: this.t("places-user_form-permission_denied") || "Você não tem permissão para acessar esta página.",
      });
      this.$router.push('/dashboard');
      return;
    }

    document.getElementsByTagName("title")[0].innerText =
      "Kevi | " + this.t("benefitsCenter-title");
    this.loadProgram();
  },
  beforeDestroy() {
    this.setPreviewScrollLock(false);
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    openPreview() {
      this.previewOpen = true;
      this.setPreviewScrollLock(true);
    },
    closePreview() {
      this.previewOpen = false;
      this.setPreviewScrollLock(false);
    },
    togglePreview() {
      if (this.previewOpen) {
        this.closePreview();
      } else {
        this.openPreview();
      }
    },
    setPreviewScrollLock(locked) {
      const value = locked ? "hidden" : "";
      document.body.style.overflow = value;
      document.documentElement.style.overflow = value;
    },
    loadProgram() {
      this.state = "loading";
      this.$http(keviApi.getOptions("benefits/program"))
        .then((response) => {
          this.program = this.normalizeProgram(response.body.program);
          this.state = "ready";
        })
        .catch(() => {
          this.state = "error";
          this.$store.dispatch("notification/error", {
            text: this.t("benefitsCenter-loadError"),
          });
        });
    },
    saveSettingsSection(section, payload) {
      if (!section || !payload || typeof payload !== "object") return;

      this.$http(keviApi.getOptions(`benefits/program/sections/${section}`, "PATCH", payload))
        .then((response) => {
          if (response.body.program) {
            this.program = this.normalizeProgram(response.body.program);
          }

          this.$store.dispatch("notification/success", {
            text: this.t("benefitsCenter-saveSuccess"),
          });
        })
        .catch(() => {
          this.$store.dispatch("notification/error", {
            text: this.t("benefitsCenter-saveError"),
          });
        });
    },
    saveReward(reward) {
      const isNew = !reward.id;
      const payload = { ...reward };
      if (isNew) {
        delete payload.id;
      }

      const requestConfig = isNew
        ? keviApi.getOptions("rewards", "POST", payload)
        : keviApi.getOptions(`rewards/${reward.id}`, "PATCH", payload);

      this.$http(requestConfig)
        .then((response) => {
          const dbReward = response.body.reward;
          const previousReward = this.program.points.items.find(
            (candidate) => candidate.id === dbReward.id.toString() || candidate.id === reward.id
          );
          const mappedReward = {
            id: dbReward.id.toString(),
            hasRedemptions: Boolean(previousReward && previousReward.hasRedemptions),
            active: !!dbReward.active,
            name: dbReward.name,
            subtitle: dbReward.subtitle,
            description: dbReward.description,
            points: dbReward.price,
            privacy: dbReward.privacy,
            startDate: dbReward.start_date,
            endDate: dbReward.end_date,
            canUse: !!dbReward.self_usable,
            successMessage: dbReward.client_success_message || "",
            hasUsageInstructions: !!dbReward.usage_instructions,
            usageInstructions: dbReward.usage_instructions || "",
            image: dbReward.image ? { url: dbReward.image } : null,
          };

          if (isNew) {
            this.program.points.items.push(mappedReward);
            this.$store.dispatch("notification/success", {
              text: this.t("benefitsCenter-points-panel-createSuccess") || "Recompensa criada com sucesso.",
            });
          } else {
            const index = this.program.points.items.findIndex(
              (candidate) => candidate.id === mappedReward.id || candidate.id === reward.id
            );
            if (index >= 0) {
              this.$set(this.program.points.items, index, mappedReward);
            }
            this.$store.dispatch("notification/success", {
              text: this.t("benefitsCenter-points-panel-editSuccess") || "Recompensa salva com sucesso.",
            });
          }
        })
        .catch(() => {
          this.$store.dispatch("notification/error", {
            text: isNew
              ? this.t("benefitsCenter-points-panel-createError") || "Erro ao criar recompensa."
              : this.t("benefitsCenter-points-panel-editError") || "Erro ao atualizar recompensa.",
          });
        });
    },
    deleteReward(reward) {
      if (!reward || !reward.id) return;
      
      this.$http(keviApi.getOptions(`rewards/${reward.id}`, "DELETE"))
        .then(() => {
          const index = this.program.points.items.findIndex((candidate) => candidate.id === reward.id);
          if (index >= 0) {
            this.program.points.items.splice(index, 1);
          }
          this.$store.dispatch("notification/success", {
            text: this.t("benefitsCenter-points-panel-deleteSuccess") || "Recompensa excluída com sucesso.",
          });
        })
        .catch(() => {
          this.$store.dispatch("notification/error", {
            text: this.t("benefitsCenter-saveError"),
          });
        });
    },

    getDefaultProgram() {
      return {
        name: "Club Kevi",
        logo: "",
        font: "default",
        primaryColor: "#2aaa4c",
        banners: [],
        settings: {
          showStatement: true,
          customCss: false,
        },
        login: {
          background: {
            type: "solid",
            color: "#2aaa4c",
            image: null,
          },
          form: {
            firstNameRequired: true,
            lastNameRequired: true,
            birthDateRequired: true,
            emailRequired: true,
          },
        },

        faq: {
          title: "",
          showTitle: true,
          image: null,
          showImage: true,
          intro: "",
          showIntro: true,
          items: [],
        },
        terms: "",
        tiers: {
          enabled: true,
          items: [],
        },
        points: {
          enabled: false,
          pointsFactor: 1,
          maxPointsPerOrder: null,
          items: [],
        },
      };
    },
    normalizeProgram(program) {
      const defaultProgram = this.getDefaultProgram();
      const safeProgram = program || {};

      return {
        ...defaultProgram,
        ...safeProgram,
        primaryColor: safeProgram.primaryColor,
        settings: {
          ...defaultProgram.settings,
          ...(safeProgram.settings || {}),
        },
        login: {
          ...defaultProgram.login,
          ...(safeProgram.login || {}),
          background: {
            ...defaultProgram.login.background,
            ...(safeProgram.login?.background || {}),
          },
          form: {
            ...defaultProgram.login.form,
            ...(safeProgram.login?.form || {}),
          },
        },
        tiers: {
          ...defaultProgram.tiers,
          ...(safeProgram.tiers || {}),
          items: Array.isArray(safeProgram.tiers?.items) ? safeProgram.tiers.items : [],
        },
        points: {
          ...defaultProgram.points,
          ...(safeProgram.points || {}),
          items: Array.isArray(safeProgram.points?.items) ? safeProgram.points.items : [],
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
#benefits-center-page {
  // background: #f3f4f6;
  min-height: 100%;
  //pading: 16px;
  padding-left: 16px;
  width: 100%;
}

.benefits-center-card {
  background: #fcfcfd;
  border: 1px solid #e5e7eb;
  border-radius: 24px;
  padding: 24px;
  width: 100%;
}

.benefits-center-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;

  h1 {
    color: #384250;
    font-family: "Sora", sans-serif;
    font-size: 30px;
    font-weight: 400;
    line-height: 1.3;
    margin: 0;
  }
}



.benefits-center-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.benefits-center-layout {
  align-items: flex-start;
  display: flex;
  gap: 32px;
  width: 100%;
}

.benefits-center-tab-content {
  flex: 1 1 auto;
  margin-top: 0;
  min-width: 0;
}

.benefits-center-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.benefits-center-preview--desktop {
  align-self: flex-start;
  border-left: 1px solid #e5e7eb;
  display: flex;
  flex: 0 0 390px;
  flex-direction: column;
  margin-left: 8px;
  max-height: calc(100vh - 48px);
  min-width: 280px;
  padding-left: 32px;
  position: sticky;
  top: 24px;

  ::v-deep .benefits-preview {
    flex: 1 1 auto;
    max-height: 100%;
    min-height: 0;
  }
}

.benefits-center-preview--mobile {
  display: none;
}

.benefits-center-preview-panel {
  display: contents;
}

.benefits-center-preview-fab {
  display: none;
}

.benefits-center-preview-close {
  display: none;
}

.benefits-center-preview-placeholder {
  aspect-ratio: 390 / 844;
  box-sizing: border-box;
  width: 390px;
  max-width: 100%;
  border: 1px dashed #d2d6db;
  border-radius: 24px;
  color: #6c737f;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  padding: 24px;
  text-align: center;
}

.benefits-center-empty-tab {
  align-items: center;
  border: 1px dashed #d2d6db;
  border-radius: 16px;
  color: #6c737f;
  display: flex;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  justify-content: center;
  min-height: 180px;
  padding: 24px;
  text-align: center;
}



@media (max-width: 1100px) {
  .benefits-center-layout {
    flex-direction: column;
    gap: 16px;
  }

  .benefits-center-tab-content {
    width: 100%;
  }

  .benefits-center-preview--desktop {
    display: none;
  }

  .benefits-center-preview-fab {
    align-items: center;
    background: #384250;
    border: 0;
    border-radius: 50%;
    bottom: max(24px, env(safe-area-inset-bottom));
    box-shadow: 0 8px 24px rgba(17, 25, 39, 0.24);
    cursor: pointer;
    display: inline-flex;
    height: 56px;
    justify-content: center;
    position: fixed;
    right: max(24px, env(safe-area-inset-right));
    transition: background 0.2s ease, transform 0.2s ease;
    width: 56px;
    z-index: 1110;

    &:hover {
      background: #1f2a37;
      transform: scale(1.04);
    }

    &:focus-visible {
      box-shadow: 0 0 0 3px rgba(56, 66, 80, 0.35);
      outline: none;
    }

    &.is-open {
      background: #1f2a37;
    }
  }

  .benefits-center-preview--mobile {
    background: #ffffff;
    bottom: 0;
    display: flex;
    flex-direction: column;
    height: 100dvh;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 1100;
  }

  .benefits-center-preview-mobile-body {
    box-sizing: border-box;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    padding:
      max(12px, env(safe-area-inset-top))
      max(16px, env(safe-area-inset-right))
      max(12px, env(safe-area-inset-bottom))
      max(16px, env(safe-area-inset-left));

    ::v-deep .benefits-preview--fullscreen {
      flex: 1 1 auto;
      min-height: 0;
    }
  }
}

.preview-mobile-enter-active,
.preview-mobile-leave-active {
  transition: opacity 0.24s ease, transform 0.24s ease;
}

.preview-mobile-enter,
.preview-mobile-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

.preview-mobile-enter-to,
.preview-mobile-leave {
  opacity: 1;
  transform: translateY(0);
}

@media (max-width: 767px) {
  #benefits-center-page {
    padding-left: 0;
  }

  .benefits-center-card {
    border-radius: 16px;
    padding: 16px;
  }

  .benefits-center-header {
    margin-bottom: 16px;

    h1 {
      font-size: 24px;
    }
  }

  .benefits-center-content,
  .benefits-center-layout {
    gap: 16px;
  }
}

@media (max-width: 480px) {
  .benefits-center-card {
    border-left: 0;
    border-radius: 0;
    border-right: 0;
    padding: 14px;
  }

  .benefits-center-preview-placeholder {
    border-radius: 16px;
    max-height: 420px;
    padding: 16px;
  }
}
</style>
